<template>
  <div class="container page recharge-page">
    <van-nav-bar :title="$t('recharge.recharge')" class="nav-bar">
      <template #left><van-icon name="arrow-left" color="#f3c7a2" @click="back" /></template>
    </van-nav-bar>

    <div class="balance-card">
      <div>{{ $t('recharge.curr_balance') }}</div>
      <strong>{{ balance }}</strong>
    </div>

    <div class="account-card">
      <h3>{{ $t('recharge.receiving_account') }}</h3>
      <div v-if="baseInfo.recharge_account_name" class="account-row">
        <span>{{ $t('recharge.account_name') }}</span><b>{{ baseInfo.recharge_account_name }}</b>
      </div>
      <div v-if="baseInfo.recharge_account" class="account-row">
        <span>{{ $t('recharge.account_number') }}</span>
        <div class="account-value">
          <b>{{ baseInfo.recharge_account }}</b>
          <button type="button" class="copy-btn" @click="copyText(baseInfo.recharge_account)">
            <van-icon name="records" />{{ $t('recharge.copy') }}
          </button>
        </div>
      </div>
      <div v-if="baseInfo.recharge_address" class="account-row">
        <span>{{ $t('recharge.recharge_address') }}</span>
        <div class="account-value">
          <b>{{ baseInfo.recharge_address }}</b>
          <button type="button" class="copy-btn" @click="copyText(baseInfo.recharge_address)">
            <van-icon name="records" />{{ $t('recharge.copy') }}
          </button>
        </div>
      </div>
      <p v-if="baseInfo.recharge_note" class="account-note">{{ baseInfo.recharge_note }}</p>
      <p v-if="!hasAccount" class="account-note warning">{{ $t('recharge.account_unavailable') }}</p>
    </div>

    <div class="form-card">
      <label>{{ $t('recharge.input_money') }}</label>
      <van-field v-model="money" type="number" input-align="right" :placeholder="$t('recharge.input_money')" />

      <label>{{ $t('recharge.payment_proof') }}</label>
      <label class="upload-box">
        <input ref="proofInput" type="file" accept="image/jpeg,image/png,image/gif" @change="chooseProof" />
        <img v-if="proofPreview" :src="proofPreview" alt="proof" />
        <div v-else><van-icon name="photograph" /><span>{{ $t('recharge.choose_proof') }}</span></div>
      </label>

      <van-button round block class="submit-btn" :loading="submitting" :disabled="!hasAccount" @click="submit">
        {{ $t('recharge.submit_application') }}
      </van-button>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      balance: 0,
      money: '',
      proofFile: null,
      proofPreview: '',
      baseInfo: {},
      submitting: false
    };
  },
  computed: {
    hasAccount() {
      return !!(this.baseInfo.recharge_account || this.baseInfo.recharge_address);
    }
  },
  methods: {
    back() { window.history.back(); },
    async copyText(value) {
      if (!value) return;
      let copied = false;
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(String(value));
          copied = true;
        } catch (error) {
          copied = false;
        }
      }
      if (!copied) {
        const textarea = document.createElement('textarea');
        textarea.value = String(value);
        textarea.setAttribute('readonly', 'readonly');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          copied = document.execCommand('copy');
        } catch (error) {
          copied = false;
        }
        document.body.removeChild(textarea);
      }
      this.$toast(this.$t(copied ? 'recharge.copied' : 'recharge.copy_failed'));
    },
    loadBaseInfo() {
      this.$http({ method: 'get', url: 'base_info' }).then(res => {
        if (res && res.code === 200) this.baseInfo = res.data || {};
      });
    },
    chooseProof(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      if (!/^image\/(jpeg|png|gif)$/.test(file.type)) {
        this.$toast(this.$t('recharge.invalid_proof'));
        event.target.value = '';
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$toast(this.$t('recharge.proof_too_large'));
        event.target.value = '';
        return;
      }
      if (this.proofPreview) URL.revokeObjectURL(this.proofPreview);
      this.proofFile = file;
      this.proofPreview = URL.createObjectURL(file);
    },
    async submit() {
      const amount = Number(this.money);
      if (!this.hasAccount) return this.$toast(this.$t('recharge.account_unavailable'));
      if (!amount || amount <= 0) return this.$toast(this.$t('reservation.money_err'));
      if (!this.proofFile) return this.$toast(this.$t('recharge.proof_required'));
      this.submitting = true;
      try {
        const formData = new FormData();
        formData.append('file', this.proofFile);
        const upload = await this.$http({ method: 'post', url: 'upload_recharge_proof', data: formData });
        if (!upload || upload.code !== 200) {
          this.$toast((upload && upload.msg) || this.$t('recharge.upload_failed'));
          return;
        }
        const result = await this.$http({
          method: 'post',
          url: 'recharge',
          data: { money: amount.toFixed(2), proof: upload.data }
        });
        if (result && result.code === 200) {
          this.$toast(result.msg || this.$t('recharge.submitted'));
          this.$router.replace('/RechargeList');
        } else {
          this.$toast((result && result.msg) || this.$t('recharge.submit_failed'));
        }
      } finally {
        this.submitting = false;
      }
    }
  },
  created() {
    if (!localStorage.getItem('token')) return this.$router.replace('/Login');
    this.balance = this.$route.params.balance || 0;
    this.loadBaseInfo();
  },
  beforeDestroy() {
    if (this.proofPreview) URL.revokeObjectURL(this.proofPreview);
  }
};
</script>

<style lang="less" scoped>
@import "../../assets/css/base.css";
.recharge-page { background:#1f1c1f; color:#f3c7a2; overflow:auto; }
.balance-card,.account-card,.form-card { margin:22px; padding:26px; border:1px solid #766350; border-radius:14px; background:linear-gradient(120deg,#212121,#313131); }
.balance-card { display:flex; justify-content:space-between; font-size:28px; }
.balance-card strong { font-size:32px; }
.account-card h3 { margin:0 0 20px; font-size:30px; }
.account-row { display:flex; justify-content:space-between; gap:20px; padding:14px 0; border-bottom:1px solid #4c443c; font-size:25px; }
.account-row b { text-align:right; word-break:break-all; }
.account-value { display:flex; align-items:center; justify-content:flex-end; gap:10px; min-width:0; max-width:68%; }
.account-value b { min-width:0; line-height:1.35; }
.copy-btn { flex:0 0 auto; display:inline-flex; align-items:center; gap:4px; padding:7px 10px; border:1px solid #d6a574; border-radius:8px; color:#68302c; background:linear-gradient(#fde3ca,#e7b486); font-size:20px; cursor:pointer; }
.copy-btn:active { opacity:.75; }
.account-note { line-height:1.5; font-size:24px; }
.warning { color:#ff8b8b; }
.form-card > label { display:block; margin:18px 0 10px; font-size:26px; }
.form-card /deep/ .van-cell { background:#242424; border:1px solid #766350; border-radius:10px; }
.form-card /deep/ input { color:#f3c7a2; }
.upload-box { min-height:210px; display:flex!important; align-items:center; justify-content:center; border:1px dashed #d6a574; border-radius:12px; cursor:pointer; overflow:hidden; }
.upload-box input { display:none; }
.upload-box img { width:100%; max-height:360px; object-fit:contain; }
.upload-box div { display:flex; flex-direction:column; align-items:center; gap:14px; font-size:25px; }
.upload-box /deep/ .van-icon { font-size:58px; }
.submit-btn { margin-top:30px; color:#68302c; font-size:28px; font-weight:bold; background:linear-gradient(#fde3ca,#e7b486); border:0; }
</style>
